import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInAnonymously,
  linkWithCredential,
  linkWithPopup,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';
import { userService } from './userService';
import { HANDOVER_POLICY } from './iosAuthHandover';

/** In flight while one anonymous sign-in is running (`ensureShareSession`). */
let pendingShareSession = null;

async function startShareSession() {
  const restored = await new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
  if (restored) return restored;
  try {
    const { user } = await signInAnonymously(auth);
    return user;
  } catch (error) {
    // `auth/admin-restricted-operation` means Anonymous sign-in is off for
    // this Firebase project — every public share link fails for every
    // visitor who isn't already logged in (housfully-prod, 2026-09-25).
    console.error(
      `[share] anonymous sign-in failed (${error?.code}); share links cannot open:`,
      error
    );
    throw error;
  }
}

export const authService = {
  async signUp(email, password, displayName, previewCode = null) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      if (displayName) {
        await updateProfile(user, { displayName });
      }

      // Create the user document WITHOUT access. Granting is server-owned:
      // redeemPreviewCode below flips hasPreviewAccess and clears
      // joinedWaitlistAt once it has validated the code itself. A client that
      // can grant itself access on its own doc can walk past the waitlist.
      await userService.createUser(user.uid, {
        email: user.email,
        displayName: displayName || '',
        hasPreviewAccess: false,
        joinedWaitlistAt: new Date(),
        onboarding: {
          completed: false,
          currentStep: 'welcomePanel',
          demoOnboardingCompleted: false,
          gestureTutorials: { orbit: false, firstPerson: false },
          nudges: {
            commandCenterIntro: false,
            designNudge: false,
            minimapCoach: false,
            layoutDemo: false,
            fixturesDemoV2: false,
            qrSheetsDemo: false,
          },
          commandCenterCommandsUsed: [],
        },
      });

      // Redeem: this both records WHICH code was used and performs the grant.
      // Reusable codes are reported too — the server decides whether to burn.
      // A failure here leaves the account on the waitlist rather than blocking
      // sign-up; the user can enter the same code again from /waitlist.
      let hasPreviewAccess = false;
      if (previewCode) {
        try {
          const redemption = await userService.redeemPreviewCode(previewCode);
          hasPreviewAccess = redemption.valid;
        } catch (error) {
          console.error('Error redeeming preview code:', error);
          // Continue anyway - user creation succeeded
        }
      }

      return { user, hasPreviewAccess };
    } catch (error) {
      throw error;
    }
  },

  /**
   * The `users` doc of a NEW Google / Apple account that skips the
   * preview-code screen because it came to reach a page (`?returnTo=` — a
   * team's join page): what that screen's "join the waitlist" writes, no
   * access granted. Email sign-up writes its own in `signUp`.
   */
  async createWaitlistUser(user) {
    await userService.createUser(user.uid, {
      email: user.email,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      hasPreviewAccess: false,
      joinedWaitlistAt: new Date(),
    });
  },

  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Update last sign in
      await userService.updateLastSignIn(userCredential.user.uid);

      return userCredential.user;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Turn the ANONYMOUS share-link visitor into a real account, keeping the uid.
   *
   * This is the whole reason the sandbox needs no re-owning on sign-up: a
   * linked anonymous user is the SAME user, so their design sandbox, its
   * structure, its rooms and the grant that reads them all still name them.
   * `createUserWithEmailAndPassword` would mint a different uid and strand
   * every one of those.
   *
   * The account is stamped `accountKind: 'homeowner'`: they came in from a
   * shared design, not from the contractor funnel, and the contractor app's
   * access gate has nothing to let them into (`PrivateRoute` sends them to
   * /my-designs instead of the waitlist).
   *
   * Note `copyDemoProjectForNewUser` is an `onCreate` AUTH trigger, and
   * linking does not create a user — so a homeowner does not get a copy of the
   * contractor demo project. That is the outcome we want; it is written down
   * because it is a consequence of linking, not a decision anywhere in code.
   *
   * @param {'email'|'google'} method
   * @returns {Promise<import('firebase/auth').User>}
   */
  async linkAnonymousToAccount(method, { email, password, displayName } = {}) {
    const current = auth.currentUser;
    if (!current) {
      throw new Error('No session to upgrade — open the link again');
    }
    if (!current.isAnonymous) return current;

    const credential =
      method === 'google'
        ? await linkWithPopup(current, new GoogleAuthProvider())
        : await linkWithCredential(
            current,
            EmailAuthProvider.credential(email, password)
          );
    const user = credential.user;

    if (displayName && !user.displayName) {
      await updateProfile(user, { displayName });
    }

    // The uid survived the link, so a `users` doc may already exist (a second
    // visit, a link retried after a network failure). Create it only when it
    // does not, and never overwrite an existing account's flags.
    const existing = await userService.getUser(user.uid);
    if (!existing) {
      await userService.createUser(user.uid, {
        email: user.email || email || '',
        displayName: displayName || user.displayName || '',
        accountKind: 'homeowner',
        hasPreviewAccess: false,
        onboarding: { completed: true },
      });
    }
    return user;
  },

  async signOut() {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      throw error;
    }
  },

  async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Check if user exists in Firestore
      const existingUser = await userService.getUser(user.uid);

      if (!existingUser) {
        // New user - return user data without creating document yet
        // The PreviewCode component will handle creating the user document
        return { user, isNewUser: true };
      } else {
        // Existing user - update last sign in
        await userService.updateLastSignIn(user.uid);
        return {
          user,
          isNewUser: false,
          hasPreviewAccess: existingUser.hasPreviewAccess,
        };
      }
    } catch (error) {
      throw error;
    }
  },

  async signInWithApple() {
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Check if user exists in Firestore
      const existingUser = await userService.getUser(user.uid);

      if (!existingUser) {
        // New user - return user data without creating document yet
        // The PreviewCode component will handle creating the user document
        return { user, isNewUser: true };
      } else {
        // Existing user - update last sign in
        await userService.updateLastSignIn(user.uid);
        return {
          user,
          isNewUser: false,
          hasPreviewAccess: existingUser.hasPreviewAccess,
        };
      }
    } catch (error) {
      throw error;
    }
  },

  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },

  getCurrentUser() {
    return auth.currentUser;
  },

  /**
   * The signed-in user's ID-token claims (`orgId`, `orgRole`, `admin`, …), or
   * `{}` with nobody signed in. The CACHED token, never a forced refresh: it
   * is the token the Firestore rules will see, so an access decision made
   * from it agrees with them (`@shared-lib/org-access`).
   */
  async getTokenClaims() {
    const user = auth.currentUser;
    if (!user) return {};
    const { claims } = await user.getIdTokenResult();
    return claims;
  },

  /**
   * A share-link visitor's session. A signed-in user (the contractor opening
   * their own link) keeps theirs; anyone else is signed in ANONYMOUSLY — a
   * real uid with no account, which is what a share grant is issued to (see
   * `functions/features/share-grants.js`). Waits for the persisted session
   * to restore first, so a returning visitor keeps the uid their grant names.
   *
   * The app treats an anonymous user as signed out (`isAccountUser`).
   *
   * Callers that ask at the same time share ONE sign-in. A first-time visitor
   * opening a walkthrough link asks twice as the page mounts (the link check
   * and the returning-visitor lookup in `useDesignSandbox`); two anonymous
   * sign-ins made two uids, the share grant went to one, the page kept the
   * other, and the rules refused the walkthrough ("could not be loaded").
   */
  ensureShareSession() {
    if (!pendingShareSession) {
      pendingShareSession = startShareSession().finally(() => {
        pendingShareSession = null;
      });
    }
    return pendingShareSession;
  },

  async sendPasswordResetEmail(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  },

  async verifyAndSignInWithToken(idToken, uid) {
    try {
      console.log('[Auth] Starting verifyAndSignInWithToken...');

      // Try using fetch directly instead of Firebase SDK to avoid potential WKWebView issues
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/createCustomTokenFromIdToken`;

      console.log('[Auth] Calling Cloud Function via fetch:', functionUrl);
      const startTime = Date.now();

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        HANDOVER_POLICY.attemptTimeoutMs
      );

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: { idToken, uid },
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log(
        `[Auth] Cloud Function responded in ${Date.now() - startTime}ms, status: ${response.status}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Auth] Cloud Function error:', errorText);
        // The status tells a refused token (4xx) from a failure worth retrying.
        const error = new Error(`Cloud Function failed: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const responseData = await response.json();
      console.log('[Auth] Cloud Function response received');

      // The callable function wraps response in { result: { ... } }
      const result = { data: responseData.result || responseData };

      if (result.data.customToken) {
        console.log(
          '[Auth] Got custom token, calling signInWithCustomToken...'
        );
        const signInStart = Date.now();
        // Now sign in with the custom token
        const userCredential = await signInWithCustomToken(
          auth,
          result.data.customToken
        );
        console.log(
          `[Auth] signInWithCustomToken completed in ${Date.now() - signInStart}ms`
        );
        const user = userCredential.user;

        // Skip Firestore operations for faster auth - they can happen later
        console.log('[Auth] Skipping Firestore user check for speed');
        return user;
      } else {
        throw new Error('Failed to create custom token');
      }
    } catch (error) {
      // Check if user is already authenticated as a fallback
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === uid) {
        return currentUser;
      }

      throw error;
    }
  },
};
